import { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import { debounce } from 'throttle-debounce';
import { Dayjs } from 'dayjs';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import { AppUIActions } from 'app/store/AppUI/AppUI.slice';
import { BillingInvoicesActions } from 'app/store/BillingInvoices/BillingInvoices.slice';
// Selectors
import { selectInvoicesSidebarOpen } from 'app/store/AppUI/AppUI.selectors';
import { selectAccountsAsOptions } from 'app/store/AccountsManagement/AccountsManagement.selectors';
import { selectParams, selectValidateDateRange } from 'app/store/BillingInvoices/BillingInvoices.selectors';
// Mui
import { Box, Typography, FormHelperText, Divider } from '@mui/material';
// Components
import SidebarToggleButton from 'app/components/SidebarToggleButton';
import { Input, Select, DesktopDatePicker } from 'app/components/Mui';
import { Autocomplete } from 'app/components/Mui/Autocompletes';
import QuickDateChips from 'app/components/QuickDateChips';
// i18next
import { useTranslation } from 'react-i18next';
// Constant
import { SIDEBAR_WIDTH } from 'app/App.constants';
// Utilities
import { adjustDateRange } from 'app/utilities/DateTime';

const InvoicesSidebar:FC = () => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const invoicesSidebarOpen = useAppSelector(selectInvoicesSidebarOpen)
  const accountOptions = useAppSelector(selectAccountsAsOptions);
  const params = useAppSelector(selectParams);
  const validationErrors = useAppSelector((state) => selectValidateDateRange(state, { isRequired: true }));

  const [ search, setSearch ] = useState('');

  // eslint-disable-next-line
  const debounceParams = useCallback(debounce(1000, (field:keyof typeof params, value:any) => {
    dispatch(BillingInvoicesActions.setParams({ [field]: value, offset: 0 }));
  }), []);
  
  // eslint-disable-next-line
  const debouncePeriodParams = useCallback(debounce(1000, (period: 'start' | 'end', value: Dayjs | null, oppositeValue: Dayjs | null) => {
    const { start, end } = adjustDateRange(period, value, oppositeValue);

    dispatch(BillingInvoicesActions.setParams({ dateRangeStart: start, dateRangeEnd: end, offset: 0 }));
  }), []);

  const handleAccountChange = (value:any) => {
    debounceParams('accountId', value);
  }

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if ( name === 'search' ) setSearch(value);
    debounceParams(name as any, value);
  }

  const handleChangeStatus = (event:ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    const status = value === 'overdue' ? 'sent' : value;

    const updates:Record<string, any> = {
      status,
      overdue: value === 'overdue' ? true : null
    };

    dispatch(BillingInvoicesActions.setParams({ ...updates, offset: 0 }));
  }

  const statusValue = useMemo(() => {
    if (params.overdue && params.status === 'sent') return 'overdue';
    return params.status;
  }, [params.status, params.overdue]);
  
  const handleClearSearch = () => {
    setSearch('');
    dispatch(BillingInvoicesActions.setParams({ search: '', offset: 0 }));
  }

  const handleDateChange = (period:'start' | 'end') => (newValue:Dayjs | null) => {
    const opositeValue = period === 'start' ? params.dateRangeEnd : params.dateRangeStart;
    debouncePeriodParams(period, newValue, opositeValue);
  }

  const handleDateRangeChange = (start: Dayjs | null, end: Dayjs | null) => {
    dispatch(BillingInvoicesActions.setParams({ dateRangeStart: start, dateRangeEnd: end, offset: 0 }));
  };

  const handleToggleInvoiceSidebar = () => {
    dispatch(AppUIActions.toggleInvoicesSidebarOpen());
  }

  const statusOptions = [
    { id: 'all', name: t('labels.all') },
    { id: 'draft', name: t('labels.draft') },
    { id: 'paid', name: t('labels.paid') },
    { id: 'sent', name: t('labels.outstanding') },
    { id: 'overdue', name: t('labels.overdue') },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        bgcolor: 'white',
        borderRight: '1px solid rgba(0,0,0,0.08)',
        zIndex: 1,
        marginLeft: invoicesSidebarOpen ? 0 : `-${SIDEBAR_WIDTH}px`
      }}
    >
      <Box sx={{
        flexGrow: 1,
        p: 4,
        overflowY: 'auto'
      }}>
        <Autocomplete
          value={params.accountId}
          onChange={handleAccountChange}
          options={accountOptions || []}
          TextFieldProps={{
            label: t('labels.companyAccount')
          }}
          renderOption={(props, option) => (
            <li {...props} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} key={option.value}>
              <Typography variant="subtitle1">{option.label}</Typography>
              {option.props.address ? (
                <Typography variant="caption">{option.props.address}</Typography>
              ) : null}
            </li>
          )}
        />
        <Input
          label={t('labels.search')} name="search" value={search}
          placeholder={t('form.placeholder.caseClaimOrInvoiceNumber')}
          onChange={handleChange}
          onClear={handleClearSearch}
        />
        <Select
          label={t('labels.status')} name="status" value={statusValue}
          onChange={handleChangeStatus}
          options={statusOptions}
        />
        <Divider sx={{ mt: 2, mx: -4 }} />
        <DesktopDatePicker
          label={t('labels.start')} name="dateRangeStart" value={params.dateRangeStart}
          onChange={handleDateChange('start')}
          slotProps={{
            textField: {
              error: Boolean(validationErrors && validationErrors.startError),
              helperText: validationErrors?.startError || ''
            }
          }}
        />
        <DesktopDatePicker
          label={t('labels.end')} name="dateRangeEnd" value={params.dateRangeEnd}
          onChange={handleDateChange('end')}
          slotProps={{
            textField: {
              error: Boolean(validationErrors && validationErrors.endError),
              helperText: validationErrors?.endError || ''
            }
          }}
        />
        {validationErrors && validationErrors.rangeError ? (
          <FormHelperText error>{validationErrors.rangeError}</FormHelperText>
        ) : null}
        <QuickDateChips onChange={handleDateRangeChange} />
      </Box>
      <SidebarToggleButton
        name="Billing invoices sidebar toggle button"
        open={invoicesSidebarOpen}
        onClick={handleToggleInvoiceSidebar}
      />
    </Box>
  )
}

export default InvoicesSidebar;
